import { type FC } from "react";
import { useQuery } from "@apollo/client";
import { Box, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BannerQuery } from "@/shared/graphql";
import type { DeviceMode } from "../../model/types";
import { byDevice } from "./byDevice";

export interface BackgroundImageProps {
  desktopImageId?: string | null;
  mobileImageId?: string | null;
  device: DeviceMode;
  align: "left" | "center" | "right";
  verticalAlign: "top" | "center" | "bottom";
  brightness?: number | null;
}

const StyledPicture = styled(Box, {
  shouldForwardProp: (prop) =>
    !["objectPosition", "brightness"].includes(prop as string),
})<{ objectPosition: string; brightness?: number | null }>(
  ({ theme, objectPosition, brightness }) => ({
    zIndex: -1,
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    height: "100%",
    width: "100%",
    filter:
      brightness === null || brightness === undefined
        ? undefined
        : theme.palette.mode === "dark"
          ? `brightness(${brightness})`
          : `opacity(${brightness})`,

    "& img": {
      objectPosition,
      objectFit: "cover",
      height: "100%",
      width: "100%",
    },
  })
);

/**
 * Картинка фона. next/image витрины заменён обычным img: кабинет собирается
 * Vite, оптимизатора Next тут нет. Мобильный режим без своей картинки берёт
 * десктопную, как на витрине.
 */
export const BackgroundImage: FC<BackgroundImageProps> = ({
  desktopImageId,
  mobileImageId,
  device,
  align,
  verticalAlign,
  brightness,
}) => {
  const { t } = useTranslation();
  const imageId = byDevice(device, {
    mobile: mobileImageId || desktopImageId,
    desktop: desktopImageId,
  });

  const { data } = useQuery(BannerQuery, {
    variables: { id: imageId },
    skip: !imageId,
    fetchPolicy: "cache-and-network",
  });

  const url = data?.file?.url;

  if (!imageId || !url) {
    return null;
  }

  return (
    <StyledPicture
      data-testid="CoverBackgroundImage"
      objectPosition={[align, verticalAlign].join(" ")}
      brightness={brightness}
    >
      <img src={url} alt={t("platformCustomCovers.preview.mediaAlt")} />
    </StyledPicture>
  );
};
